import { c as _c } from "react/compiler-runtime";
import ProfileAvatar, { ProfileBadgeSize } from "@/components/common/profile/ProfileAvatar";
import UserCICAndLevel, { UserCICAndLevelSize } from "@/components/user/utils/UserCICAndLevel";
import { DropAuthorBadges } from "@/components/waves/drops/DropAuthorBadges";
import IdentityProfileSupplement from "@/components/waves/drops/identity/IdentityProfileSupplement";
import { getDropIdentityFallbackValue, getDropIdentityProfile } from "@/components/waves/drops/identityDisplay.helpers";
import ParticipationIdentityProfileCard from "@/components/waves/drops/participation/ParticipationIdentityProfileCard";
import type { ParticipationIdentityProfileCardVariant } from "@/components/waves/drops/participation/ParticipationIdentityProfileCard";
import type { ApiDropResolvedIdentityProfile } from "@/generated/models/ApiDropResolvedIdentityProfile";
import { shortenAddress } from "@/helpers/address.helpers";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import Link from "next/link";
type WaveLeaderboardIdentityVariant = "responsive" | "condensed";
interface WaveLeaderboardIdentityProps {
  readonly drop: ExtendedDrop;
  readonly variant: WaveLeaderboardIdentityVariant;
  readonly cardVariant?: ParticipationIdentityProfileCardVariant | undefined;
  readonly className?: string | undefined;
  readonly showIdentityHeader?: boolean | undefined;
  readonly supplementFullWidth?: boolean | undefined;
}
interface WaveLeaderboardIdentitySummaryProps {
  readonly profile: ApiDropResolvedIdentityProfile | null;
  readonly fallbackValue: string | null;
  readonly contextId?: string | number | undefined;
  readonly showIdentityHeader?: boolean | undefined;
  readonly supplementFullWidth?: boolean | undefined;
}
function WaveLeaderboardIdentitySummary(t0) {
  const $ = _c(32);
  const {
    profile,
    fallbackValue,
    contextId,
    showIdentityHeader: t1,
    supplementFullWidth: t2
  } = t0;
  const showIdentityHeader = t1 === undefined ? true : t1;
  t2 === undefined ? false : t2;
  const displayLabel = profile?.handle ?? profile?.primary_address ?? fallbackValue;
  if (!displayLabel) {
    return null;
  }
  let t3;
  if ($[0] !== displayLabel || $[1] !== profile) {
    t3 = profile ? `/${encodeURIComponent(displayLabel.toLowerCase())}` : null;
    $[0] = displayLabel;
    $[1] = profile;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const rootHref = t3;
  const primaryAddress = profile?.primary_address;
  let t4;
  if ($[3] !== primaryAddress || $[4] !== profile) {
    t4 = !!profile?.handle && !!primaryAddress && primaryAddress.toLowerCase() !== profile.handle.toLowerCase();
    $[3] = primaryAddress;
    $[4] = profile;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const shouldShowAddress = t4;
  let t5;
  if ($[6] !== primaryAddress || $[7] !== shouldShowAddress) {
    t5 = shouldShowAddress ? shortenAddress(primaryAddress) : null;
    $[6] = primaryAddress;
    $[7] = shouldShowAddress;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const displayAddress = t5;
  let t6;
  if ($[9] !== profile?.bio || $[10] !== profile?.top_rep_categories) {
    t6 = !!profile?.bio?.trim() || (profile?.top_rep_categories ?? []).some(_temp);
    $[9] = profile?.bio;
    $[10] = profile?.top_rep_categories;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const hasSupplementContent = t6;
  const showBelowHeaderSupplement = !!profile && hasSupplementContent;
  let t7;
  if ($[12] !== displayLabel) {
    t7 = displayLabel.slice(0, 1);
    $[12] = displayLabel;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t7) {
    t8 = <span className="tw-text-[11px] tw-font-semibold tw-uppercase tw-text-iron-100">{t7}</span>;
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  const avatarFallback = t8;
  if (profile && !showIdentityHeader && !hasSupplementContent) {
    return null;
  }
  let t9;
  if ($[16] !== avatarFallback || $[17] !== contextId || $[18] !== displayAddress || $[19] !== displayLabel || $[20] !== primaryAddress || $[21] !== profile || $[22] !== rootHref || $[23] !== showIdentityHeader) {
    t9 = showIdentityHeader && <div className="tw-flex tw-items-start tw-gap-3">{rootHref ? <Link href={rootHref} prefetch={false} onClick={_temp2} className="tw-block tw-flex-shrink-0 tw-self-start tw-no-underline" aria-label={`View ${displayLabel}'s profile`}><ProfileAvatar pfpUrl={profile?.pfp ?? null} size={ProfileBadgeSize.SMALL} alt={`${displayLabel} avatar`} fallbackContent={avatarFallback} /></Link> : <ProfileAvatar pfpUrl={null} size={ProfileBadgeSize.SMALL} alt={`${displayLabel} avatar`} fallbackContent={avatarFallback} />}<div className="tw-min-w-0 tw-flex-1"><div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-2 tw-gap-y-1">{rootHref ? <Link href={rootHref} prefetch={false} onClick={_temp3} className="tw-max-w-full tw-text-sm tw-font-semibold tw-leading-none tw-text-iron-50 tw-no-underline desktop-hover:hover:tw-text-iron-300"><span className="tw-block tw-truncate">{displayLabel}</span></Link> : <span className="tw-break-all tw-text-sm tw-font-semibold tw-leading-none tw-text-iron-50">{displayLabel}</span>}{profile && <><UserCICAndLevel level={profile.level} size={UserCICAndLevelSize.SMALL} /><DropAuthorBadges profile={profile} tooltipIdPrefix={`leaderboard-identity-${contextId ?? profile.id}`} /></>}</div>{displayAddress && <p title={primaryAddress} className="tw-mb-0 tw-mt-1 tw-font-mono tw-text-xs tw-tracking-[0.01em] tw-text-iron-400">{displayAddress}</p>}</div></div>;
    $[16] = avatarFallback;
    $[17] = contextId;
    $[18] = displayAddress;
    $[19] = displayLabel;
    $[20] = primaryAddress;
    $[21] = profile;
    $[22] = rootHref;
    $[23] = showIdentityHeader;
    $[24] = t9;
  } else {
    t9 = $[24];
  }
  let t10;
  if ($[25] !== profile || $[26] !== showBelowHeaderSupplement || $[27] !== showIdentityHeader) {
    t10 = showBelowHeaderSupplement && <div className={showIdentityHeader ? "tw-mt-2" : undefined}><IdentityProfileSupplement profile={profile} variant="compact" maxRepCategories={2} /></div>;
    $[25] = profile;
    $[26] = showBelowHeaderSupplement;
    $[27] = showIdentityHeader;
    $[28] = t10;
  } else {
    t10 = $[28];
  }
  let t11;
  if ($[29] !== t10 || $[30] !== t9) {
    t11 = <div data-testid="wave-leaderboard-identity-summary" className="tw-rounded-lg tw-border tw-border-solid tw-border-white/5 tw-bg-iron-900 tw-p-3">{t9}{t10}</div>;
    $[29] = t10;
    $[30] = t9;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  return t11;
}
function _temp3(event_0) {
  return event_0.stopPropagation();
}
function _temp2(event) {
  return event.stopPropagation();
}
function _temp(category) {
  return category.category.trim().length > 0;
}
export function WaveLeaderboardIdentity(t0) {
  const $ = _c(31);
  const {
    drop,
    variant,
    cardVariant,
    className,
    showIdentityHeader: t1,
    supplementFullWidth: t2
  } = t0;
  const showIdentityHeader = t1 === undefined ? true : t1;
  const supplementFullWidth = t2 === undefined ? false : t2;
  let t3;
  if ($[0] !== drop.metadata || $[1] !== drop.wave) {
    t3 = getDropIdentityProfile({
      wave: drop.wave,
      metadata: drop.metadata
    });
    $[0] = drop.metadata;
    $[1] = drop.wave;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const identityProfile = t3;
  let t4;
  if ($[3] !== drop.metadata || $[4] !== drop.wave || $[5] !== identityProfile) {
    t4 = identityProfile ? null : getDropIdentityFallbackValue({
      wave: drop.wave,
      metadata: drop.metadata
    });
    $[3] = drop.metadata;
    $[4] = drop.wave;
    $[5] = identityProfile;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const fallbackValue = t4;
  if (!identityProfile && !fallbackValue) {
    return null;
  }
  if (variant === "responsive" && identityProfile) {
    let t5;
    if ($[7] !== cardVariant || $[8] !== drop.id || $[9] !== identityProfile || $[10] !== showIdentityHeader || $[11] !== supplementFullWidth) {
      t5 = <div data-testid="wave-leaderboard-identity-full" className="tw-hidden lg:tw-block"><ParticipationIdentityProfileCard profile={identityProfile} contextId={drop.id} variant={cardVariant} showIdentityHeader={showIdentityHeader} supplementFullWidth={supplementFullWidth} /></div>;
      $[7] = cardVariant;
      $[8] = drop.id;
      $[9] = identityProfile;
      $[10] = showIdentityHeader;
      $[11] = supplementFullWidth;
      $[12] = t5;
    } else {
      t5 = $[12];
    }
    let t6;
    if ($[13] !== drop.id || $[14] !== identityProfile || $[15] !== showIdentityHeader || $[16] !== supplementFullWidth) {
      t6 = <div className="lg:tw-hidden"><WaveLeaderboardIdentitySummary profile={identityProfile} fallbackValue={null} contextId={drop.id} showIdentityHeader={showIdentityHeader} supplementFullWidth={supplementFullWidth} /></div>;
      $[13] = drop.id;
      $[14] = identityProfile;
      $[15] = showIdentityHeader;
      $[16] = supplementFullWidth;
      $[17] = t6;
    } else {
      t6 = $[17];
    }
    let t7;
    if ($[18] !== className || $[19] !== t5 || $[20] !== t6) {
      t7 = <div className={className}>{t5}{t6}</div>;
      $[18] = className;
      $[19] = t5;
      $[20] = t6;
      $[21] = t7;
    } else {
      t7 = $[21];
    }
    return t7;
  }
  let t5;
  if ($[22] !== drop.id || $[23] !== fallbackValue || $[24] !== identityProfile || $[25] !== showIdentityHeader || $[26] !== supplementFullWidth) {
    t5 = <WaveLeaderboardIdentitySummary profile={identityProfile} fallbackValue={fallbackValue} contextId={drop.id} showIdentityHeader={showIdentityHeader} supplementFullWidth={supplementFullWidth} />;
    $[22] = drop.id;
    $[23] = fallbackValue;
    $[24] = identityProfile;
    $[25] = showIdentityHeader;
    $[26] = supplementFullWidth;
    $[27] = t5;
  } else {
    t5 = $[27];
  }
  let t6;
  if ($[28] !== className || $[29] !== t5) {
    t6 = <div className={className}>{t5}</div>;
    $[28] = className;
    $[29] = t5;
    $[30] = t6;
  } else {
    t6 = $[30];
  }
  return t6;
}